/**
 * ADMIN LICENSE DETAIL PAGE
 *
 * View and manage a single license with full details
 */

import { useState } from 'react';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Key,
  Copy,
  Check,
  ArrowLeft,
  Building2,
  MonitorSmartphone,
  Cloud,
  Workflow,
  Loader2,
  User,
  Mail,
  AlertTriangle,
  Ban,
  RefreshCw,
  CalendarPlus,
  Trash2,
  Clock,
  Activity,
} from 'lucide-react';
import {
  useAdminLicense,
  useLicenseActivations,
  useExtendLicense,
  useSuspendLicense,
  useReactivateLicense,
  useDeactivateDevice,
} from '@/hooks/queries/useAdminLicenses';

export const Route = createFileRoute('/admin/licenses/$licenseId/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }

    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: LicenseDetailPage,
});

function LicenseDetailPage() {
  const { licenseId } = Route.useParams();
  const { data: license, isLoading } = useAdminLicense(licenseId);
  const { data: activations, isLoading: loadingActivations } = useLicenseActivations(licenseId);

  const [copied, setCopied] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [extensionMonths, setExtensionMonths] = useState(12);

  const extendLicense = useExtendLicense(licenseId);
  const suspendLicense = useSuspendLicense(licenseId);
  const reactivateLicense = useReactivateLicense(licenseId);
  const deactivateDevice = useDeactivateDevice(licenseId);

  const copyLicenseKey = () => {
    if (license?.license_key) {
      navigator.clipboard.writeText(license.license_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExtend = () => {
    if (!license) return;
    const currentExpiry = new Date(license.expires_at);
    const newExpiry = new Date(currentExpiry);
    newExpiry.setMonth(newExpiry.getMonth() + extensionMonths);
    extendLicense.mutate(newExpiry, {
      onSuccess: () => setExtendDialogOpen(false),
    });
  };

  const handleSuspend = () => {
    suspendLicense.mutate(suspendReason, {
      onSuccess: () => {
        setSuspendDialogOpen(false);
        setSuspendReason('');
      },
    });
  };

  const handleReactivate = () => {
    reactivateLicense.mutate();
  };

  const getStatusBadge = () => {
    if (!license) return null;
    const now = new Date();
    const expiresAt = new Date(license.expires_at);
    const isExpired = expiresAt <= now;

    if (license.status === 'suspended') {
      return <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-lg px-4 py-1">Suspended</Badge>;
    }

    if (isExpired || license.status === 'expired') {
      return <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-lg px-4 py-1">Expired</Badge>;
    }

    if (license.status === 'trial') {
      return <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 text-lg px-4 py-1">Trial</Badge>;
    }

    return <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-lg px-4 py-1">Active</Badge>;
  };

  const getLicenseTypeIcon = (type: string) => {
    switch (type) {
      case 'cloud':
        return <Cloud className="w-5 h-5" />;
      case 'desktop':
        return <MonitorSmartphone className="w-5 h-5" />;
      case 'hybrid':
        return <Workflow className="w-5 h-5" />;
      default:
        return <Key className="w-5 h-5" />;
    }
  };

  const getDaysUntilExpiry = () => {
    if (!license) return 0;
    const now = new Date();
    const expiry = new Date(license.expires_at);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!license) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Key className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-2xl font-bold mb-2">License Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The license you're looking for doesn't exist or has been deleted.
          </p>
          <Button asChild>
            <Link to="/admin/licenses">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Licenses
            </Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const daysUntilExpiry = getDaysUntilExpiry();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/licenses">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                License Details
                {getStatusBadge()}
              </h1>
              <p className="text-muted-foreground">
                {license.company?.name || 'Unknown Company'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {license.status === 'suspended' ? (
              <Button onClick={handleReactivate} disabled={reactivateLicense.isPending}>
                {reactivateLicense.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Reactivate
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setExtendDialogOpen(true)}>
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Extend
                </Button>
                <Button variant="outline" className="text-orange-600 hover:text-orange-700" onClick={() => setSuspendDialogOpen(true)}>
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* License Key Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  License Key
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                  <code className="flex-1 text-lg font-mono font-semibold">
                    {license.license_key}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyLicenseKey}>
                    {copied ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {/* License metadata */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getLicenseTypeIcon(license.license_type)}
                      <span className="font-semibold capitalize">{license.license_type}</span>
                    </div>
                  </div>

                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Plan</p>
                    <p className="font-semibold mt-1">{license.plan?.name || 'Unknown'}</p>
                    <Badge variant="outline" className="mt-1">Tier {license.plan?.tier_level}</Badge>
                  </div>

                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Devices</p>
                    <p className="font-semibold mt-1">
                      {license.activationsCount || 0} / {license.max_devices}
                    </p>
                  </div>

                  <div className={`p-3 rounded-lg ${daysUntilExpiry < 30 && daysUntilExpiry > 0 ? 'bg-orange-50 dark:bg-orange-950/20' : daysUntilExpiry <= 0 ? 'bg-red-50 dark:bg-red-950/20' : 'bg-muted/50'}`}>
                    <p className="text-xs text-muted-foreground">Expires In</p>
                    <p className={`font-semibold mt-1 ${daysUntilExpiry < 30 && daysUntilExpiry > 0 ? 'text-orange-600' : daysUntilExpiry <= 0 ? 'text-red-600' : ''}`}>
                      {daysUntilExpiry > 0 ? `${daysUntilExpiry} days` : `Expired ${Math.abs(daysUntilExpiry)} days ago`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company & Owner Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Company & Owner
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Company Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Company</h4>
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-semibold">{license.company?.name || 'Unknown Company'}</p>
                          <p className="text-sm text-muted-foreground">{license.company?.email || 'No email'}</p>
                        </div>
                      </div>
                      {license.company?.phone && (
                        <p className="text-sm text-muted-foreground pl-8">{license.company.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Owner Info */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Owner</h4>
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                      {license.companyOwner ? (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <User className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{license.companyOwner.full_name}</p>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              {license.companyOwner.email}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No owner assigned</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Device Activations */}
            {license.license_type !== 'cloud' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MonitorSmartphone className="w-5 h-5" />
                    Device Activations ({activations?.length || 0})
                  </CardTitle>
                  <CardDescription>
                    Devices that have activated this license
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingActivations ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : activations && activations.length > 0 ? (
                    <div className="space-y-3">
                      {activations.map((activation) => (
                        <div
                          key={activation.id}
                          className={`p-4 rounded-lg border ${activation.is_active ? 'bg-white dark:bg-gray-950' : 'bg-muted/50 opacity-60'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <MonitorSmartphone className="w-4 h-4" />
                                <span className="font-semibold">{activation.device_name}</span>
                                {activation.is_active ? (
                                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactive</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{activation.os_info}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                ID: {activation.device_fingerprint.substring(0, 20)}...
                              </p>
                            </div>
                            <div className="text-right space-y-1">
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Last seen: {formatRelativeTime(activation.last_seen_at)}
                              </div>
                              {activation.is_active && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => deactivateDevice.mutate(activation.device_fingerprint)}
                                  disabled={deactivateDevice.isPending}
                                >
                                  {deactivateDevice.isPending ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      Deactivate
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <MonitorSmartphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No devices have activated this license yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-emerald-500" />
                    <div>
                      <p className="font-medium text-sm">License Issued</p>
                      <p className="text-xs text-muted-foreground">{formatDate(license.issued_at)}</p>
                    </div>
                  </div>

                  {license.activated_at && (
                    <div className="flex gap-3">
                      <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                      <div>
                        <p className="font-medium text-sm">First Activation</p>
                        <p className="text-xs text-muted-foreground">{formatDate(license.activated_at)}</p>
                      </div>
                    </div>
                  )}

                  {license.last_verified_at && (
                    <div className="flex gap-3">
                      <div className="w-2 h-2 mt-2 rounded-full bg-purple-500" />
                      <div>
                        <p className="font-medium text-sm">Last Verification</p>
                        <p className="text-xs text-muted-foreground">{formatDate(license.last_verified_at)}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className={`w-2 h-2 mt-2 rounded-full ${daysUntilExpiry <= 0 ? 'bg-red-500' : 'bg-gray-300'}`} />
                    <div>
                      <p className="font-medium text-sm">
                        {daysUntilExpiry <= 0 ? 'Expired' : 'Expires'}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(license.expires_at)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan Features */}
            {license.plan && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Plan Features</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Max Users</span>
                    <span className="font-medium">{license.plan.max_users}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Invoices/Month</span>
                    <span className="font-medium">{license.plan.max_invoices_per_month}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly Price</span>
                    <span className="font-medium">{license.plan.price_monthly_dzd?.toLocaleString()} DZD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Yearly Price</span>
                    <span className="font-medium">{license.plan.price_yearly_dzd?.toLocaleString()} DZD</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Admin Notes */}
            {license.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Admin Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{license.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Extend License Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend License</DialogTitle>
            <DialogDescription>
              Add more time to this license's expiration date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Expiry</Label>
              <p className="text-sm font-medium">{formatDate(license.expires_at)}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="extension">Extend By</Label>
              <select
                id="extension"
                value={extensionMonths}
                onChange={(e) => setExtensionMonths(Number(e.target.value))}
                className="w-full px-3 py-2 border border-border rounded-md bg-background"
              >
                <option value={1}>1 month</option>
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>1 year</option>
                <option value={24}>2 years</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>New Expiry Date</Label>
              <p className="text-sm font-medium text-emerald-600">
                {(() => {
                  const current = new Date(license.expires_at);
                  const newDate = new Date(current);
                  newDate.setMonth(newDate.getMonth() + extensionMonths);
                  return formatDate(newDate.toISOString());
                })()}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExtend} disabled={extendLicense.isPending}>
              {extendLicense.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarPlus className="w-4 h-4 mr-2" />
              )}
              Extend License
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend License Dialog */}
      <AlertDialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Suspend License
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately suspend access for all devices using this license.
              The customer will not be able to use the software until the license is reactivated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Reason for suspension</Label>
            <Input
              id="reason"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="e.g., Payment overdue, Terms violation..."
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSuspend}
              disabled={suspendLicense.isPending || !suspendReason}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {suspendLicense.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Ban className="w-4 h-4 mr-2" />
              )}
              Suspend License
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

export default LicenseDetailPage;
